import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  CHART_PALETTE,
  fetchFleet,
  formatMinutes,
  SEVERITY_COLORS,
} from "../../lib/reports";
import { downloadCsv, reportFilename } from "../../lib/csv";
import ExportCsvButton from "./ExportCsvButton";

function Skeleton() {
  return <div className="h-48 animate-pulse rounded bg-slate-100" />;
}

function NoData() {
  return (
    <div className="flex h-40 items-center justify-center rounded border border-dashed border-slate-200 text-sm text-slate-500">
      No data for this period
    </div>
  );
}

function Section({ title, subtitle, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-md border border-slate-200 bg-white shadow-card"
    >
      <div className="border-b border-slate-200 px-4 py-3">
        <div className="font-heading text-sm font-semibold text-slate-900">
          {title}
        </div>
        {subtitle && (
          <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
            {subtitle}
          </div>
        )}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-slate-200 bg-white p-4 shadow-card"
    >
      <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className="font-heading text-2xl font-bold text-slate-900 tabular-nums mt-1.5">
        {value}
      </div>
    </div>
  );
}

const SEVERITY_ORDER = ["critical", "high", "medium", "low", "info"];

export default function FleetTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    setErr(null);
    fetchFleet(params)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        setErr(e?.response?.data?.detail || "Failed to load fleet");
        setLoading(false);
      });
  };

  useEffect(load, [params.date_from, params.date_to]);

  if (loading) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    );
  }
  if (err) {
    return (
      <div
        data-testid="fleet-error"
        className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
      >
        {err}{" "}
        <button onClick={load} className="ml-3 underline" data-testid="fleet-retry">
          Retry
        </button>
      </div>
    );
  }
  if (!data) return null;

  // Sort severities in canonical order so the chart reads critical → info.
  const sevSorted = [...(data.alerts_by_severity || [])].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
  );

  const exportCsv = () => {
    const rows = [];
    rows.push(["Counts", "Total alerts", data.total_alerts]);
    rows.push(["Counts", "Avg ack (minutes)", data.avg_ack_minutes ?? ""]);
    rows.push(["Counts", "Avg resolution (minutes)", data.avg_resolution_minutes ?? ""]);
    rows.push(["Counts", "Open RMAs", data.open_rmas]);
    rows.push(["Counts", "RMAs closed in range", data.rmas_closed_in_range]);
    sevSorted.forEach((r) => rows.push(["AlertsBySeverity", r.severity, r.count]));
    (data.alerts_by_vendor || []).forEach((r) =>
      rows.push(["AlertsByVendor", r.vendor, r.count])
    );
    (data.alerts_by_type || []).forEach((r) =>
      rows.push(["AlertsByType", r.alert_type, r.count])
    );
    (data.alerts_by_status || []).forEach((r) =>
      rows.push(["AlertsByStatus", r.status, r.count])
    );
    (data.rmas_by_vendor || []).forEach((r) =>
      rows.push(["RMAsByVendor", r.vendor, r.count])
    );
    (data.rmas_by_status || []).forEach((r) =>
      rows.push(["RMAsByStatus", r.status, r.count])
    );
    downloadCsv(
      reportFilename("fleet", params),
      ["Section", "Key", "Value"],
      rows
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExportCsvButton onExport={exportCsv} testId="export-csv-fleet" />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Total alerts"
          value={data.total_alerts}
          testId="fleet-total"
        />
        <StatCard
          label="Avg ack time"
          value={formatMinutes(data.avg_ack_minutes)}
          testId="fleet-avg-ack"
        />
        <StatCard
          label="Avg resolution"
          value={formatMinutes(data.avg_resolution_minutes)}
          testId="fleet-avg-resolution"
        />
        <StatCard
          label="Open RMAs"
          value={data.open_rmas}
          testId="fleet-open-rmas"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section
          title="Alerts by severity"
          subtitle="critical → info"
          testId="fleet-by-severity"
        >
          {sevSorted.length === 0 ? (
            <NoData />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={sevSorted}
                layout="vertical"
                margin={{ top: 5, right: 20, bottom: 5, left: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="severity"
                  tick={{ fontSize: 11 }}
                  width={70}
                />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {sevSorted.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={SEVERITY_COLORS[entry.severity] || "#64748b"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section
          title="Alerts by vendor"
          subtitle="upstream platform"
          testId="fleet-by-vendor"
        >
          {(data.alerts_by_vendor || []).length === 0 ? (
            <NoData />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={data.alerts_by_vendor}
                margin={{ top: 5, right: 10, bottom: 5, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="vendor" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {data.alerts_by_vendor.map((_, i) => (
                    <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section
          title="Top alert types"
          subtitle="most-frequent fault categories"
          testId="fleet-by-type"
        >
          {(data.alerts_by_type || []).length === 0 ? (
            <NoData />
          ) : (
            <ol className="space-y-2">
              {data.alerts_by_type.map((row, i) => (
                <li
                  key={row.alert_type}
                  data-testid={`fleet-type-${row.alert_type}`}
                  className="flex items-center justify-between rounded border border-slate-200 px-3 py-2"
                >
                  <span className="flex items-center gap-3">
                    <span className="font-data text-[10px] text-slate-400">
                      #{i + 1}
                    </span>
                    <span className="text-sm text-slate-800">
                      {row.alert_type.replace(/_/g, " ")}
                    </span>
                  </span>
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold tabular-nums text-slate-800">
                    {row.count}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Section>

        <Section
          title="RMAs by vendor"
          subtitle="warranty claim distribution"
          testId="fleet-rmas-by-vendor"
        >
          {(data.rmas_by_vendor || []).length === 0 ? (
            <NoData />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={data.rmas_by_vendor}
                margin={{ top: 5, right: 10, bottom: 5, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="vendor" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {data.rmas_by_vendor.map((_, i) => (
                    <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>
      </div>

      <Section
        title="RMAs by status"
        subtitle="claim pipeline"
        testId="fleet-rmas-by-status"
      >
        {(data.rmas_by_status || []).length === 0 ? (
          <NoData />
        ) : (
          <div className="flex flex-wrap gap-2">
            {data.rmas_by_status.map((row) => (
              <div
                key={row.status}
                data-testid={`fleet-rma-status-${row.status}`}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5"
              >
                <span className="font-data text-[10px] uppercase tracking-wide text-slate-600">
                  {row.status.replace(/_/g, " ")}
                </span>
                <span className="font-heading text-sm font-bold text-slate-900 tabular-nums">
                  {row.count}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
